import * as React from 'react';

export interface BiasErrorPoint {
	/** Etichetta del punto, mostrata sotto agli estremi e a metà serie. */
	label: string;
	/** Scostamento previsto/reale in %, positivo se il modello sovrastima. */
	biasPct: number;
}

export interface BiasErrorChartProps {
	/** Serie giornaliera dello scostamento. Vuota = nessun confronto disponibile. */
	points: BiasErrorPoint[];
	/** Testo mostrato quando la serie è vuota. */
	emptyLabel?: string;
}

const WIDTH = 240;
const HEIGHT = 80;
/** Ordinata dello zero: la banda utile va da 4 a 72, quindi ±34 px di escursione. */
const ZERO_Y = 38;
const SPAN_Y = 34;
/**
 * Scostamento minimo rappresentato a fondo scala. Senza un pavimento, una serie
 * quasi perfetta (±0,3%) verrebbe normalizzata sul proprio massimo e disegnata
 * come un'oscillazione violenta: il grafico direbbe "modello instabile" proprio
 * quando il modello è preciso.
 */
const MIN_SCALE_PCT = 5;

/** Scostamento previsto/reale giorno per giorno, con la linea dello zero. */
const BiasErrorChart = ({ points, emptyLabel }: BiasErrorChartProps) => {
	if (points.length === 0) {
		return (
			<svg
				width="100%"
				height={HEIGHT}
				viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
				xmlns="http://www.w3.org/2000/svg"
				className="font-mono"
				aria-label="Bias vs reale - nessun dato"
			>
				<title>Bias vs reale - nessun dato</title>
				<line
					x1="0"
					y1={ZERO_Y}
					x2={WIDTH}
					y2={ZERO_Y}
					stroke="color-mix(in srgb, var(--color-accent) 30%, transparent)"
					strokeWidth="1"
					strokeDasharray="3,2"
				/>
				<text x={WIDTH / 2} y={ZERO_Y - 8} fontSize="8" fill="#64748b" textAnchor="middle">
					{emptyLabel ?? 'Nessun confronto disponibile'}
				</text>
			</svg>
		);
	}

	const scale = Math.max(MIN_SCALE_PCT, ...points.map((p) => Math.abs(p.biasPct)));
	const step = points.length > 1 ? WIDTH / (points.length - 1) : 0;
	const pts = points.map((p, i) => ({
		x: points.length > 1 ? i * step : WIDTH / 2,
		y: ZERO_Y - (p.biasPct / scale) * SPAN_Y,
	}));

	const first = pts[0] ?? { x: 0, y: ZERO_Y };
	const last = pts[pts.length - 1] ?? first;

	let linePath = `M${first.x.toFixed(1)},${first.y.toFixed(1)}`;
	for (let i = 1; i < pts.length; i++) {
		const p = pts[i];
		const prev = pts[i - 1];
		if (!p || !prev) continue;
		const dx = (p.x - prev.x) / 3;
		linePath += ` C${(prev.x + dx).toFixed(1)},${prev.y.toFixed(1)} ${(p.x - dx).toFixed(1)},${p.y.toFixed(1)} ${p.x.toFixed(1)},${p.y.toFixed(1)}`;
	}
	const areaPath = `${linePath} L${last.x.toFixed(1)},${ZERO_Y} L${first.x.toFixed(1)},${ZERO_Y} Z`;

	const startLabel = points[0]?.label ?? '';
	const midLabel = points[Math.floor((points.length - 1) / 2)]?.label ?? '';
	const endLabel = points[points.length - 1]?.label ?? '';

	return (
		<svg
			width="100%"
			height={HEIGHT}
			viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
			xmlns="http://www.w3.org/2000/svg"
			className="font-mono"
			aria-label={`Bias vs reale - ${points.length} giorni, scala ±${Math.round(scale)}%`}
		>
			<title>{`Bias vs reale - ${points.length} giorni`}</title>
			{/* 0% reference */}
			<line
				x1="0"
				y1={ZERO_Y}
				x2={WIDTH}
				y2={ZERO_Y}
				stroke="color-mix(in srgb, var(--color-accent) 30%, transparent)"
				strokeWidth="1"
				strokeDasharray="3,2"
			/>
			{/* Area fill fra la curva e lo zero */}
			<path d={areaPath} fill="rgba(16,185,129,0.07)" />
			{/* Bias line */}
			<path
				d={linePath}
				fill="none"
				stroke="#10b981"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
			<text x="2" y="10" fontSize="8" fill="#64748b">
				Bias vs reale
			</text>
			<text
				x="2"
				y={ZERO_Y + 6}
				fontSize="7"
				fill="color-mix(in srgb, var(--color-accent) 50%, transparent)"
			>
				0%
			</text>
			<text x={WIDTH - 2} y="10" fontSize="7" fill="#64748b" textAnchor="end">
				±{Math.round(scale)}%
			</text>
			<text x="2" y="78" fontSize="7" fill="#64748b">
				{startLabel}
			</text>
			<text x={WIDTH / 2} y="78" fontSize="7" fill="#64748b" textAnchor="middle">
				{midLabel}
			</text>
			<text x={WIDTH - 2} y="78" fontSize="7" fill="#64748b" textAnchor="end">
				{endLabel}
			</text>
		</svg>
	);
};
BiasErrorChart.displayName = 'BiasErrorChart';

export { BiasErrorChart };
